import type { HostCampaignConfig } from '@playcastdotio/Messaging';
import type { InstanceCapabilities } from '../../services/playjector/interfaces/IPlayjectorService';
import {
  hostCapacityConfigState,
  isOpsCapacityPinned,
} from './hostCapacityConfig';

/**
 * Raise local host `maxPlayers` so an android v2 campaign's
 * `gameInstancesPerHost` is not clamped by the default capacity of 1.
 *
 * Only raises (never lowers) the configured ceiling. Caps at the machine's
 * reported `maxGameInstances` when known, otherwise 64 (wire max).
 *
 * Ops-pinned capacity (`MATCHMAKING_V2_HOST_CAPACITY_CONFIG` / dashboard
 * maxPlayers) is a hard ceiling — campaign assign must not raise past it, or
 * a machine set to 2 would still launch gameInstancesPerHost=4.
 */
export function alignHostCapacityForCampaign(
  campaign: Pick<HostCampaignConfig, 'gameInstancesPerHost' | 'matchmakingVersion'>,
  capabilities: InstanceCapabilities | undefined,
): number {
  const current = hostCapacityConfigState.currentState.maxPlayers;
  const requested = campaign.gameInstancesPerHost ?? 1;
  if (campaign.matchmakingVersion !== 'v2' || requested <= 1) {
    return current;
  }

  // Dashboard / API maxPlayers wins over campaign gameInstancesPerHost.
  if (isOpsCapacityPinned()) {
    return current;
  }

  const machineCeiling = capabilities?.maxGameInstances !== undefined && capabilities.maxGameInstances > 0
    ? capabilities.maxGameInstances
    : 64;
  const needed = Math.min(requested, machineCeiling);

  if (needed > current) {
    // Soft raise for unconfigured hosts (default 1) — do not mark ops-pinned.
    hostCapacityConfigState.updateState({ maxPlayers: needed });
  }

  return hostCapacityConfigState.currentState.maxPlayers;
}
